import { Injectable } from '@angular/core';
import { PersonsDaoService } from '../persons-dao.service';
import { Person } from 'src/app/_model/person';
import { SQLiteDBConnection } from '@capacitor-community/sqlite';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { IMSId } from 'src/app/_model/ims-object';
import { PersonsDbTableDefinition } from './persons-db-table-definition';
import { LocalPersonsDbTableDefinition } from './local-persons-db-table-definition';
import { LocalPersonOverridesDbTableDefinition } from './local-person-overrides-db-table-definition';

@Injectable()
export class PersonsDbDaoService implements PersonsDaoService {
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private personsTableDefinition: PersonsDbTableDefinition,
    private localPersonsTableDefinition: LocalPersonsDbTableDefinition,
    private localPersonOverridesTableDefinition: LocalPersonOverridesDbTableDefinition,
  ) {}

  public async savePersons(persons: Person[]): Promise<void> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      const queries: Query[] = persons.map((person) => ({
        statement:
          'INSERT OR REPLACE INTO ' +
          this.personsTableDefinition.tableName +
          '(id, label, name, lastname, fullname, email, primaryphonenumber, branchid, branchlabel, databag)' +
          ' VALUES (?,?,?,?,?,?,?,?,?,?)',
        parameters: [
          Number(person.getImsId()),
          person.label,
          person.name,
          person.lastname,
          person.fullname,
          person.email,
          person.primaryphonenumber,
          person.branch?.getImsId(),
          person.branch?.label,
          person.databag ? JSON.stringify(person.databag) : undefined,
        ],
      }));
      await this.dbPersistenceService.executeBatch(db, queries);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error saving persons to db',
        error?.message,
      );
    }
  }

  public async createPerson(person: Person): Promise<Person> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      const query: Query = {
        statement:
          'INSERT INTO ' +
          this.localPersonsTableDefinition.tableName +
          '(label, name, lastname, fullname, email, primaryphonenumber, branchid, branchlabel, databag)' +
          ' VALUES (?,?,?,?,?,?,?,?,?)',
        parameters: [
          person.label,
          person.name,
          person.lastname,
          person.fullname,
          person.email,
          person.primaryphonenumber,
          person.branch?.getImsId(),
          person.branch?.label,
          person.databag ? JSON.stringify(person.databag) : undefined,
        ],
      };

      await this.dbPersistenceService.executeStatement(db, query);
      const res = await this.dbPersistenceService.executeQuery(db, {
        statement: 'select last_insert_rowid() as id',
        parameters: [],
      });
      if (res && res.values) {
        person.id.secondaryId = res.values.shift()?.id;
      }
      return person;
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error creating local person',
        error?.message,
      );
      return undefined;
    }
  }

  public async updateLocalPerson(person: Person): Promise<void> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'UPDATE ' +
          this.localPersonsTableDefinition.tableName +
          ' SET label = ?, name = ?, lastname = ?, fullname = ?, email = ?, primaryphonenumber = ?' +
          ' WHERE id = (?);',
        parameters: [
          person.label,
          person.name,
          person.lastname,
          person.fullname,
          person.email,
          person.primaryphonenumber,
          person.id.secondaryId,
        ],
      });
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error updating local person',
        error?.message,
      );
    }
  }

  public async savePersonOverride(person: Person): Promise<void> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'INSERT OR REPLACE INTO ' +
          this.localPersonOverridesTableDefinition.tableName +
          '(id, label, name, lastname, fullname, email, primaryphonenumber)' +
          ' VALUES (?,?,?,?,?,?,?)',
        parameters: [
          Number(person.getImsId()),
          person.label,
          person.name,
          person.lastname,
          person.fullname,
          person.email,
          person.primaryphonenumber,
        ],
      });
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error saving person override',
        error?.message,
      );
    }
  }

  public async deleteLocalPerson(id: IMSId): Promise<void> {
    const db = await this.dbPersistenceService.getImsDb();
    try {
      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'DELETE FROM ' + this.localPersonsTableDefinition.tableName + ' WHERE id = (?);',
        parameters: [id.secondaryId],
      });
    } catch (error) {
      this.logger.error(this.constructor.name, 'Error deleting local person', id, error?.message);
    }
  }

  public async getPersons(): Promise<Person[]> {
    const db = await this.dbPersistenceService.getImsDb();
    const persons: Person[] = [];
    const overrides = await this.getPersonOverrides(db);

    // Synced persons - isolated try/catch so a problem reading the local
    // table can never wipe out the already-loaded synced ones.
    try {
      const data = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.personsTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        const row = data.values[i];
        // A local edit wins over the synced values - the sync knows nothing
        // of it until the enclosing form has been submitted and the backend's
        // postprocessor has written it onto the person. An override row is a
        // complete snapshot of the editable fields, so it replaces them as a
        // block: falling back per-field would make clearing one impossible.
        // Branch and databag are never edited and always come from the sync.
        const edited = overrides.get(Number(row['id'])) ?? row;
        persons.push(
          Person.fromDto(
            {
              id: row['id'],
              label: edited['label'],
              name: edited['name'],
              lastname: edited['lastname'],
              fullname: edited['fullname'],
              email: edited['email'],
              primaryphonenumber: edited['primaryphonenumber'],
              branch: row['branchid'] != undefined
                ? { id: row['branchid'], label: row['branchlabel'] }
                : undefined,
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
          ),
        );
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error reading synced persons from db',
        error?.message,
      );
    }

    // Locally created persons (not yet on the server, no real imsId)
    try {
      const localData = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.localPersonsTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < localData.values.length; i++) {
        const row = localData.values[i];
        persons.push(
          Person.fromDto(
            {
              id: undefined,
              secondaryid: row['id'],
              label: row['label'],
              name: row['name'],
              lastname: row['lastname'],
              fullname: row['fullname'],
              email: row['email'],
              primaryphonenumber: row['primaryphonenumber'],
              branch: row['branchid'] != undefined
                ? { id: row['branchid'], label: row['branchlabel'] }
                : undefined,
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
          ),
        );
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error reading local persons from db',
        error?.message,
      );
    }

    return persons;
  }

  /**
   * Local edits to synced persons, by ims id. Isolated try/catch like the
   * two person reads - if this table can't be read, the persons themselves
   * should still load, just without their local edits applied.
   */
  private async getPersonOverrides(db: SQLiteDBConnection): Promise<Map<number, any>> {
    const overrides = new Map<number, any>();
    try {
      const data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' + this.localPersonOverridesTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        const row = data.values[i];
        overrides.set(Number(row['id']), row);
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error reading person overrides from db',
        error?.message,
      );
    }
    return overrides;
  }

  public async reset(): Promise<void> {
    // Unlike clear() (used during a sync), a Reset should wipe locally
    // created, not-yet-synced persons too - and with them the local edits
    // to synced persons, which are just as unsaved from the backend's view.
    const db = await this.dbPersistenceService.getImsDb();
    for (const tableName of [
      this.localPersonsTableDefinition.tableName,
      this.localPersonOverridesTableDefinition.tableName,
    ]) {
      this.logger.log(this.constructor.name, 'truncate table', tableName);
      await this.dbPersistenceService.truncateTable(db, tableName);
    }
  }

  public async clear(): Promise<void> {
    // Only the synced table is cleared here - locally created persons are
    // not pushed to the backend yet, so they must survive a resync. The
    // override table must survive it too: re-applying those edits on top of
    // the freshly synced rows is exactly what it exists for.
    const db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.personsTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.personsTableDefinition.tableName,
    );
  }
}
